import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Fahrzeug, GenehmigungPolizzenEinsicht, GenehmigungPolizzenSondertarif, GenehmigungSchadenEinsicht, Haushalt, Kundenverbund, Person, PersonResult, Polizze, Schaden, Schadenbeteiligung, Vertreter, ZulassungResult } from '@pezi/entities';
import { KundendatenPersonBeziehung } from '@pezi/entities/dist/src/main/ts/models/generated/kundendatenadmin/kundendatenpersonbeziehung';
import { FormatNumberPipe } from '../../../shared/pipe/format-number-pipe';
import { Tab, TabConfig } from '../../../shared/tab-bar/tab-bar.component';
import { StringUtil } from '../../util/string.util';

@Injectable()
export class PersistentTabUtil {

  private static formatNumberPipe: FormatNumberPipe = new FormatNumberPipe();
  private static datePipe: DatePipe = new DatePipe('de-AT');

  public static getPolizzeTabConfig(police: Polizze, loading?: boolean): TabConfig {
    return {
      idURL:       '/polizze/' + police.id,
      fallbackURL: '/polizze/' + police.id,
      icon:        police.locked ? 'lock' : 'description',
      label:       this.formatNumberPipe.transform(police.polNr, 'polizze') + ' - ' + police.vke.key,
      sublabel: this.datePipe.transform(police.gueltigAbDatum, 'dd.MM.y') + ' - '
                  + (police.bearbeitungsArt ? police.bearbeitungsArt.key : 'unbekannt')
                  + (police.variante && police.variante.key > 5000 && police.variante.key < 5030 ? ' ' + police.variante.value : ''),
      loading:  loading
    };
  }

  public static getMaklerPolizzeTabConfig(police: Polizze): TabConfig {
    return {
      idURL:       '/maklerpolizze/' + police.id,
      fallbackURL: '/maklerpolizze/' + police.id,
      icon:        police.locked ? 'lock' : 'description',
      label:       this.formatNumberPipe.transform(police.polNr, 'polizze') + ' - ' + police.vke.key,
      sublabel:    this.datePipe.transform(police.gueltigAbDatum, 'dd.MM.y') + ' - ' + (police.bearbeitungsArt ? police.bearbeitungsArt.key : 'unbekannt')
    };
  }

  public static getPolizzeVertragTabConfig(police: Polizze, vertragId: number): TabConfig {
    return {
      idURL:       '/polizze/' + police.id,
      fallbackURL: '/polizze/' + police.id + '/vertrag/' + vertragId,
      icon:        police.locked ? 'lock' : 'description',
      label:       this.formatNumberPipe.transform(police.polNr, 'polizze') + ' - ' + police.vke.key,
      sublabel: this.datePipe.transform(police.gueltigAbDatum, 'dd.MM.y') + ' - ' + (police.bearbeitungsArt ? police.bearbeitungsArt.key : 'unbekannt')
                  + (police.variante && police.variante.key > 5000 && police.variante.key < 5030 ? ' ' + police.variante.value : '')
    };
  }

  public static getPolizzePositionTabConfig(police: Polizze, vertragId: number, positionId: number): TabConfig {
    return {
      idURL:       '/polizze/' + police.id,
      fallbackURL: '/polizze/' + police.id + '/vertrag/' + vertragId + '/position/' + positionId,
      icon:        police.locked ? 'lock' : 'description',
      label:       this.formatNumberPipe.transform(police.polNr, 'polizze') + ' - ' + police.vke.key,
      sublabel: this.datePipe.transform(police.gueltigAbDatum, 'dd.MM.y') + ' - '
                  + (police.bearbeitungsArt ? police.bearbeitungsArt.key : 'unbekannt')
                  + (police.variante && police.variante.key > 5000 && police.variante.key < 5030 ? ' ' + police.variante.value : ''),
    };
  }

  public static getPolizzeBuchungTabConfig(police: Polizze, buchungId: number): TabConfig {
    return {
      idURL:       '/polizze/' + police.id,
      fallbackURL: '/polizze/' + police.id + '/buchung/' + buchungId,
      icon:        police.locked ? 'lock' : 'description',
      label:       this.formatNumberPipe.transform(police.polNr, 'polizze') + ' - ' + police.vke.key,
      sublabel:    this.datePipe.transform(police.gueltigAbDatum, 'dd.MM.y') + ' - ' + (police.bearbeitungsArt ? police.bearbeitungsArt.key : 'unbekannt')
    };
  }

  public static getPolizzeZahlungTabConfig(police: Polizze, zahlungId: number): TabConfig {
    return {
      idURL:       '/polizze/' + police.id,
      fallbackURL: '/polizze/' + police.id + '/zahlung/' + zahlungId,
      icon:        police.locked ? 'lock' : 'description',
      label:       this.formatNumberPipe.transform(police.polNr, 'polizze') + ' - ' + police.vke.key,
      sublabel:    this.datePipe.transform(police.gueltigAbDatum, 'dd.MM.y') + ' - ' + (police.bearbeitungsArt ? police.bearbeitungsArt.key : 'unbekannt')
    };
  }

  public static getPersonTabConfig(person: Person | PersonResult, version?: number): TabConfig {
    const versBez = person.version ? (100000 - person.version).toString() : '';
    return {
      idURL:       '/person/' + person.id,
      fallbackURL: '/person/' + person.id + (version ? ('/version/' + version) : ''),
      icon:        'person_pin',
      label:       StringUtil.getPersonDesc(person),
      sublabel: (person.juristisch ? (person.firmenbuchNr ? person.firmenbuchNr : ' - ')
        : (person.geburtsDatum ? this.datePipe.transform(person.geburtsDatum, 'dd.MM.y') : ' - ')) + (versBez ? ' V' + versBez : '')
    };
  }

  public static getMaklerPersonTabConfig(person: Person): TabConfig {
    return {
      idURL:       '/maklerperson/' + person.id,
      fallbackURL: '/maklerperson/' + person.id,
      icon:        'person_pin',
      label:       StringUtil.getPersonDesc(person),
      sublabel:    person.juristisch ? (person.firmenbuchNr ? person.firmenbuchNr : ' - ')
                  : (person.geburtsDatum ? this.datePipe.transform(person.geburtsDatum, 'dd.MM.y') : ' - ')
    };
  }

  public static getBeitragszahlerTabConfig(person: Person, version?: number): TabConfig {
    return {
      idURL:       '/beitragszahler/' + person.id,
      fallbackURL: '/beitragszahler/' + person.id + (version ? ('/version/' + version) : ''),
      icon:        'monetization_on',
      label:       StringUtil.getPersonDesc(person),
      sublabel:    person.juristisch ? (person.firmenbuchNr ? person.firmenbuchNr : ' - ')
                  : (person.geburtsDatum ? this.datePipe.transform(person.geburtsDatum, 'dd.MM.y') : ' - ')
    };
  }

  public static getVersPersonTabConfig(person: Person, version?: number): TabConfig {
    return {
      idURL:       '/versicherteperson/' + person.id,
      fallbackURL: '/versicherteperson/' + person.id + (version ? ('/version/' + version) : ''),
      icon:        'person_outline',
      label:       StringUtil.getPersonDesc(person),
      sublabel:    person.juristisch ? (person.firmenbuchNr ? person.firmenbuchNr : ' - ')
                  : (person.geburtsDatum ? this.datePipe.transform(person.geburtsDatum, 'dd.MM.y') : ' - ')
    };
  }

  public static getVertreterTabConfig(pers: Vertreter, version?: number): TabConfig {
    return {
      idURL:       '/vertreter/' + pers.person.id,
      fallbackURL: '/vertreter/' + pers.person.id + (version ? ('/version/' + version) : ''),
      icon:        'business_center',
      label:       StringUtil.getPersonDesc(pers.person),
      sublabel:    pers.person.typspeNr ? (+(pers.person.typspeNr)).toString() : ' - '
    };
  }

  public static getMaklerVertreterTabConfig(person: PersonResult): TabConfig {
    return {
      idURL:       '/maklervertreter/' + person.id,
      fallbackURL: '/maklervertreter/' + person.id,
      icon:        'business_center',
      label:       StringUtil.getPersonDesc(person),
      sublabel:    person.typspeNr ? (+(person.typspeNr)).toString() : ' - '
    };
  }

  public static getSchadenTabConfig(schaden: Schaden): TabConfig {
    return {
      idURL:       '/schaden/' + schaden.id,
      fallbackURL: '/schaden/' + schaden.id,
      icon:        'quickreply',
      label:       this.formatNumberPipe.transform(schaden.schNr, 'schaden'),
      sublabel:    (schaden.schadenDatum ? this.datePipe.transform(schaden.schadenDatum, 'dd.MM.y') + ' - ' : '') + schaden.status
    };
  }

  public static getMaklerSchadenTabConfig(schaden: Schaden): TabConfig {
    return {
      idURL:       '/maklerschaden/' + schaden.id,
      fallbackURL: '/maklerschaden/' + schaden.id,
      icon:        'quickreply',
      label:       this.formatNumberPipe.transform(schaden.schNr, 'schaden'),
      sublabel:    (schaden.schadenDatum ? this.datePipe.transform(schaden.schadenDatum, 'dd.MM.y') + ' - ' : '') + schaden.status
    };
  }

  public static getFahrzeugTabConfig(fz: Fahrzeug, version?: number): TabConfig {
    return {
      idURL:       '/fahrzeug/' + fz.id,
      fallbackURL: '/fahrzeug/' + fz.id + (version ? ('/version/' + version) : ''),
      icon:        'directions_car',
      label:       fz.kennzeichen,
      sublabel:    fz.verkaufsbezeichnung ? fz.verkaufsbezeichnung : '-'
    };
  }

  public static getZulassungTabConfig(zl: ZulassungResult): TabConfig {
    return {
      idURL:       '/zulassung/' + zl.zulId + '/index/' + zl.index,
      fallbackURL: '/zulassung/' + zl.zulId + '/index/' + zl.index,
      icon:        'approval',
      label:       zl.kennzeichen ? zl.kennzeichen : 'Kz. unbekannt',
      sublabel:    zl.kfzResult?.handelsbez ? zl.kfzResult.handelsbez : '-'
    };
  }

  public static getHaushaltTabConfig(hh: Haushalt): TabConfig {
    return {
      idURL:       '/haushalt/' + hh.adresse.adresseId,
      fallbackURL: '/haushalt/' + hh.adresse.adresseId,
      icon:        'home_work',
      label:       (hh.adresse.strasse ? hh.adresse.strasse : '') + ' ' + (hh.adresse.hausnr ? hh.adresse.hausnr : ''),
      sublabel:    hh.adresse.plz + ' ' + (hh.adresse.ortsname ? hh.adresse.ortsname : '')
    };
  }

  public static getVerbundTabConfig(kv: Kundenverbund): TabConfig {
    return {
      idURL:       '/verbund/' + kv.person.id,
      fallbackURL: '/verbund/' + kv.person.id,
      icon:        'groups',
      label:       StringUtil.getPersonDesc(kv.person),
      sublabel:    '-'
    };
  }

  public static getBetreuerTabConfig(btr: PersonResult, version?: number): TabConfig {
    return {
      idURL:       '/betreuer/' + btr.id,
      fallbackURL: '/betreuer/' + btr.id + (version ? ('/version/' + version) : ''),
      icon:        'people',
      label:       StringUtil.getPersonDesc(btr),
      sublabel:    btr.typspeNr ? btr.typspeNr : ' - '
    };
  }

  public static getMaklerBetreuerTabConfig(btr: Person): TabConfig {
    return {
      idURL:       '/maklerbetreuer/' + btr.id,
      fallbackURL: '/maklerbetreuer/' + btr.id,
      icon:        'people',
      label:       StringUtil.getPersonDesc(btr),
      sublabel:    btr.typspeNr ? btr.typspeNr : ' - '
    };
  }

  public static getBetreuerknotenTabConfig(btr: PersonResult): TabConfig {
    return {
      idURL:       '/maklerknoten/' + btr.id,
      fallbackURL: '/maklerknoten/' + btr.id,
      icon:        'join_full',
      label:       StringUtil.getPersonDesc(btr),
      sublabel:    btr.typspeNr ? btr.typspeNr : ' - '
    };
  }

  public static getPolGenehmigungTabConfig(genpol: GenehmigungPolizzenEinsicht): TabConfig {
    return {
      idURL:       '/genehmigung/MKP/' + genpol.id,
      fallbackURL: '/genehmigung/MKP/' + genpol.id,
      icon:        'approval',
      label:       this.formatNumberPipe.transform(genpol.polizzeNr, 'polizze'),
      sublabel:    this.datePipe.transform(genpol.erstellDatum, 'dd.MM.y')
    };
  }

  public static getSonderGenehmigungTabConfig(genpol: GenehmigungPolizzenSondertarif): TabConfig {
    return {
      idURL:       '/genehmigung/GO3/' + genpol.id,
      fallbackURL: '/genehmigung/GO3/' + genpol.id,
      icon:        'approval',
      label:       this.formatNumberPipe.transform(genpol.polizzeNr, 'polizze'),
      sublabel:    this.datePipe.transform(genpol.erstellDatum, 'dd.MM.y')
    };
  }

  public static getVorlDeckungGenehmigungTabConfig(genpol: GenehmigungPolizzenSondertarif): TabConfig {
    return {
      idURL:       '/genehmigung/VDO3/' + genpol.id,
      fallbackURL: '/genehmigung/VDO3/' + genpol.id,
      icon:        'approval',
      label:       this.formatNumberPipe.transform(genpol.polizzeNr, 'polizze'),
      sublabel:    this.datePipe.transform(genpol.erstellDatum, 'dd.MM.y')
    };
  }

  public static getSchadenGenehmigungTabConfig(genpol: GenehmigungSchadenEinsicht): TabConfig {
    return {
      idURL:       '/genehmigung/SCH/' + genpol.id,
      fallbackURL: '/genehmigung/SCH/' + genpol.id,
      icon:        'approval',
      label:       this.formatNumberPipe.transform(genpol.schadenNr, 'schaden'),
      sublabel:    this.datePipe.transform(genpol.erstellDatum, 'dd.MM.y')
    };
  }

  public static getPartnerTabConfig(btr: Person, version?: number): TabConfig {
    return {
      idURL:       '/partner/' + btr.id,
      fallbackURL: '/partner/' + btr.id + (version ? ('/version/' + version) : ''),
      icon:        'connect_without_contact',
      label:       StringUtil.getPersonDesc(btr),
      sublabel:    btr.perTyp ? btr.perTyp.value : '-'
    };
  }

  public static getSchadenBeteiligterTabConfig(sbt: Schadenbeteiligung, navigateAdditional: boolean): TabConfig {
    let subLbl: string;
    if (sbt.person.juristisch) {
      subLbl = sbt.person.firmenbuchNr ? sbt.person.firmenbuchNr : ' - ';
    } else {
      subLbl = sbt.person.geburtsDatum ? this.datePipe.transform(sbt.person.geburtsDatum, 'dd.MM.y') : ' - ';
    }
    return {
      idURL:    encodeURI(StringUtil.getSchadenbeteiligterNavigationStr(sbt.personId, sbt.kfzKz, sbt.fremndAnstaltNr, sbt.fremndAnstaltPolNr)),
      fallbackURL: navigateAdditional ? StringUtil.getSchadenbeteiligterNavigationStr(sbt.personId, sbt.kfzKz, sbt.fremndAnstaltNr, sbt.fremndAnstaltPolNr) :
                  StringUtil.getSchadenbeteiligterNavigationStr(sbt.personId),
      icon:     'group_add',
      label:    sbt.person ? StringUtil.getPersonDesc(sbt.person) : '-',
      sublabel: subLbl
    };
  }

  public static getKundendatenAdminTabConfig(kda: KundendatenPersonBeziehung, mode?: 'ersetzen' | 'verbund'): TabConfig {
    return {
      idURL:       '/kundendatenadmin/' + mode + '/' + kda.person.id,
      fallbackURL: '/kundendatenadmin/' + mode + '/' + kda.person.id,
      icon:        'groups',
      label:       StringUtil.getPersonDesc(kda.person),
      sublabel:    mode === 'ersetzen' ? 'Kunden ersetzen' : 'Kundenverbund'
    };
  }

}
